import React, { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { ArrowUp } from "lucide-react";
import { AnimatePresence, motion } from "framer-motion";
import Navbar from "@/components/landing/Navbar";
import NoiseOverlay from "@/components/landing/NoiseOverlay";
import Hero from "@/components/landing/Hero";
import TrustBar from "@/components/landing/TrustBar";
import Problem from "@/components/landing/Problem";
import ProductShot from "@/components/landing/ProductShot";
import HowItWorks from "@/components/landing/HowItWorks";
import Pricing from "@/components/landing/Pricing";
import WhatWeDetect from "@/components/landing/WhatWeDetect";
import FAQ from "@/components/landing/FAQ";
import Testimonials from "@/components/landing/Testimonials";
import AITrafficSection from "@/components/landing/AITrafficSection";
import FinalCTA from "@/components/landing/FinalCTA";
import Footer from "@/components/landing/Footer";

const SectionDivider = React.forwardRef<HTMLDivElement, { variant?: "cyan" | "fade" }>(({ variant = "cyan" }, ref) => (
  <div className="relative h-px w-full">
    {variant === "cyan" ? (
      <div className="absolute inset-0 bg-gradient-to-r from-transparent via-primary/40 to-transparent animate-pulse" />
    ) : (
      <div className="absolute inset-0 bg-gradient-to-r from-transparent via-border/50 to-transparent" />
    )}
  </div>
));

SectionDivider.displayName = "SectionDivider";

const Landing = () => {
  const location = useLocation();
  const [showTop, setShowTop] = useState(false);

  useEffect(() => {
    if (location.hash) {
      // Only use hash as selector if it looks like a valid CSS id selector (e.g. #pricing)
      // Skip auth tokens like #access_token=... which crash querySelector
      const hash = location.hash;
      if (/^#[a-zA-Z][\w-]*$/.test(hash)) {
        const el = document.querySelector(hash);
        if (el) {
          setTimeout(() => el.scrollIntoView({ behavior: "smooth" }), 100);
        }
      }
    }
  }, [location.hash]);

  useEffect(() => {
    const onScroll = () => setShowTop(window.scrollY > 500);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <div className="min-h-screen bg-background">
      <NoiseOverlay />
      <Navbar />
      <Hero />
      <TrustBar />
      <SectionDivider variant="fade" />
      <Problem />
      <SectionDivider variant="cyan" />
      <ProductShot />
      <SectionDivider variant="fade" />
      <HowItWorks />
      <SectionDivider variant="cyan" />
      <Pricing />
      <SectionDivider variant="fade" />
      <WhatWeDetect />
      <SectionDivider variant="cyan" />
      <AITrafficSection />
      <SectionDivider variant="fade" />
      <Testimonials />
      <SectionDivider variant="fade" />
      <FAQ />
      <SectionDivider variant="cyan" />
      <FinalCTA />
      <Footer />

      {/* Floating back-to-top */}
      <AnimatePresence>
        {showTop && (
          <motion.button
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.8 }}
            transition={{ duration: 0.2 }}
            onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
            className="fixed bottom-6 right-6 z-50 w-11 h-11 rounded-full bg-primary text-primary-foreground flex items-center justify-center shadow-lg hover:bg-primary/90 transition-colors"
            aria-label="Back to top"
          >
            <ArrowUp className="w-5 h-5" />
          </motion.button>
        )}
      </AnimatePresence>
    </div>
  );
};

export default Landing;
